/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';

import { State } from 'redux/initialState';
import { NotificationFrequency, Society } from 'types/Society';

import CustomLoading from 'modules/_shared/components/CustomLoading';
import { updateSocietyConfiguration } from 'modules/society/redux/societyActions';
import { useGetSocietySectionPermissions } from 'modules/society/hooks/useGetSocietySectionPermissions';

type NotificationConfig = {
  key: string;
  enabled: boolean;
  frequency: NotificationFrequency;
};

type SocietyNotificationsProps = {
  society: Society;
};

const NOTIFICATION_KEYS = [
  'boardPositionExpiration',
  'annualMeetingsCount',
  'quarterlyCouncilsCount',
  'accountsFormulation',
  'annualAccountsApproval',
  'incentivePlanVesting',
  'employeeStatusChange',
  'societyAdministratorRegistration',
  'partnerShareholderRegistration',
] as const;

const SocietyNotifications = ({ society }: SocietyNotificationsProps) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const { isReadOnly } = useGetSocietySectionPermissions().data;
  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );

  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationConfig[]>([]);

  useEffect(() => {
    if (actualSociety?.configuration?.notifications) {
      const notificationsConfig = actualSociety.configuration.notifications;
      const initialNotifications: NotificationConfig[] = NOTIFICATION_KEYS.map(
        (key) => ({
          key,
          enabled: notificationsConfig[key]?.enabled ?? true,
          frequency: notificationsConfig[key]?.frequency ?? 'ON_EVENT',
        })
      );
      setNotifications(initialNotifications);
    }
  }, [actualSociety]);

  const handleToggleNotification = async (key: string, enabled: boolean) => {
    if (isReadOnly) return;

    setIsLoading(true);

    const updatedNotifications = notifications.map((notif) =>
      notif.key === key ? { ...notif, enabled } : notif
    );
    setNotifications(updatedNotifications);

    const notificationConfig = {
      [`notifications.${key}`]: {
        enabled,
        frequency:
          updatedNotifications.find((n) => n.key === key)?.frequency ||
          'ON_EVENT',
      },
    };

    await dispatch(
      updateSocietyConfiguration(society._id, notificationConfig, false)
    );

    setIsLoading(false);
  };

  const getFrequencyLabel = (frequency: NotificationFrequency): string => {
    const labels: Record<NotificationFrequency, string> = {
      DAILY: t('Daily'),
      WEEKLY: t('Weekly'),
      MONTHLY: t('Monthly'),
      QUARTERLY: t('Quarterly'),
      YEARLY: t('Yearly'),
      ON_EVENT: t('OnEvent'),
    };
    return labels[frequency] || frequency;
  };

  const getNotificationLabel = (key: string): string => {
    const labels: Record<string, string> = {
      incentivePlanVesting:
        t('NotificationLabel_IncentivePlanVesting') ||
        'Vesting de unidades de Planes de Incentivos',
      boardPositionExpiration:
        t('NotificationLabel_BoardPositionExpiration') ||
        'Aviso de caducidad de cargo de Consejo de Administración',
      employeeStatusChange:
        t('NotificationLabel_EmployeeStatusChange') || 'Alta/Baja de Empleado',
      societyAdministratorRegistration:
        t('NotificationLabel_SocietyAdministratorRegistration') ||
        'Alta de administrador de Sociedad',
      partnerShareholderRegistration:
        t('NotificationLabel_PartnerShareholderRegistration') ||
        'Alta de socio/accionista en la Sociedad',
      annualMeetingsCount:
        t('NotificationLabel_AnnualMeetingsCount') || 'Nº de juntas anuales',
      quarterlyCouncilsCount:
        t('NotificationLabel_QuarterlyCouncilsCount') ||
        'Nº de consejos trimestrales',
      accountsFormulation:
        t('NotificationLabel_AccountsFormulation') || 'Formulación de cuentas',
      annualAccountsApproval:
        t('NotificationLabel_AnnualAccountsApproval') ||
        'Aprobación cuentas anuales',
    };
    return labels[key] || key;
  };

  return (
    <div className="nk-block">
      <div className="nk-block-head">
        <div className="nk-block-head-content">
          <h5 className="nk-block-title title">
            {t('Notifications') || 'Notifications'}
          </h5>
          <div className="nk-block-des">
            <p>
              {t('NotificationsDescription') ||
                'Configure the notifications for this society'}
            </p>
          </div>
        </div>
      </div>

      <div className="nk-block">
        {notifications.length > 0 && (
          <div className="card card-bordered card-preview w-100">
            <div className="table-responsive">
              <table className="nk-tb-list nk-tb-ulist">
                <thead>
                  <tr className="nk-tb-item nk-tb-head tb-tnx-head fs-11px">
                    <th className="nk-tb-col" />
                    <th className="nk-tb-col">{t('Notification')}</th>
                    <th className="nk-tb-col">{t('Frequency')}</th>
                  </tr>
                </thead>

                <tbody>
                  {notifications.map((notification) => (
                    <tr className="nk-tb-item" key={notification.key}>
                      <td className="nk-tb-col text-center">
                        <div className="custom-control custom-control-sm custom-switch d-flex justify-content-center">
                          <input
                            type="checkbox"
                            className="custom-control-input"
                            id={`notification-${notification.key}`}
                            checked={notification.enabled}
                            onChange={(e) =>
                              handleToggleNotification(
                                notification.key,
                                e.target.checked
                              )
                            }
                            disabled={isReadOnly}
                          />
                          <label
                            className="custom-control-label"
                            htmlFor={`notification-${notification.key}`}
                          />
                        </div>
                      </td>

                      <td className="nk-tb-col">
                        {getNotificationLabel(notification.key)}
                      </td>

                      <td className="nk-tb-col">
                        {getFrequencyLabel(notification.frequency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {isLoading && <CustomLoading />}
    </div>
  );
};

export default SocietyNotifications;
